import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Home, Search, MapPin, Landmark, Users, Link2, RefreshCw,
  ChevronLeft, ChevronRight, X, Building2
} from 'lucide-react';
import { folioRealService } from '../../services/folioRealService';

const initialFiltros = { folio_real: '', comuna: '', q: '', foja: '', numero: '', anio: '' };

function EstadoFolioBadge({ estado }) {
  const map = {
    vigente: { cls: 'badge-success', label: 'Vigente' },
    disponible: { cls: 'badge-warning', label: 'Disponible' },
    anulado: { cls: 'badge-error', label: 'Anulado' },
  };
  const cfg = map[estado] || { cls: 'badge-info', label: estado || 'Desconocido' };
  return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
}

function VigenteBadge({ vigente }) {
  if (vigente === null || vigente === undefined) {
    return <span className="badge badge-info">Sin dato</span>;
  }
  return vigente
    ? <span className="badge badge-success">Vigente</span>
    : <span className="badge badge-info">Transferido</span>;
}

function AlzadoBadge({ alzado }) {
  return alzado
    ? <span className="badge badge-success">Alzado</span>
    : <span className="badge badge-warning">Vigente</span>;
}

export default function FolioReal() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filtros, setFiltros] = useState(initialFiltros);
  const [resultados, setResultados] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [loadingBusqueda, setLoadingBusqueda] = useState(false);

  const [detalle, setDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const folioSeleccionado = searchParams.get('folio');

  const handleChange = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));

  const buscar = useCallback(async (paginaBuscar = 1) => {
    const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== ''));
    if (Object.keys(params).length === 0) {
      toast.error('Ingresa al menos un criterio de búsqueda.');
      return;
    }
    setLoadingBusqueda(true);
    try {
      const { data } = await folioRealService.buscar({ ...params, page: paginaBuscar });
      setResultados(data);
      setPagina(paginaBuscar);
    } catch (error) {
      console.error('Error buscando folios reales:', error);
      toast.error(error.response?.data?.message || 'Error al buscar folios reales.');
    } finally {
      setLoadingBusqueda(false);
    }
  }, [filtros]);

  const verDetalle = (idFolioReal) => {
    setSearchParams({ folio: idFolioReal });
  };

  const limpiar = () => {
    setFiltros(initialFiltros);
    setResultados(null);
    setPagina(1);
  };

  useEffect(() => {
    if (!folioSeleccionado) {
      setDetalle(null);
      return;
    }
    setLoadingDetalle(true);
    folioRealService.show(folioSeleccionado)
      .then(({ data }) => setDetalle(data.data))
      .catch((error) => {
        console.error('Error cargando el folio real:', error);
        toast.error(error.response?.data?.message || 'No se pudo cargar el folio real.');
        setDetalle(null);
      })
      .finally(() => setLoadingDetalle(false));
  }, [folioSeleccionado]);

  const onSubmit = (e) => {
    e.preventDefault();
    buscar(1);
  };

  const nombrePropietario = (i) => i.propietario_apellidos
    ? `${i.propietario_apellidos} ${i.propietario_nombres || ''}`.trim()
    : (i.propietario_nombres || '—');

  return (
    <div className="container" style={{ paddingBottom: '4rem', maxWidth: '1280px' }}>
      <header style={{ marginBottom: '2rem', marginTop: '1.5rem' }}>
        <h1 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Home style={{ color: 'var(--copper)' }} /> Folio Real
        </h1>
        <p className="section-subtitle">
          Búsqueda de inmuebles por folio real, comuna, titular o inscripción — con historial de dominio y gravámenes.
        </p>
      </header>

      {/* Buscador */}
      <section className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
        <form onSubmit={onSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.2rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Folio Real</label>
              <input type="number" placeholder="Ej: 100000" value={filtros.folio_real} onChange={handleChange('folio_real')} />
            </div>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Comuna</label>
              <input type="text" placeholder="Ej: San Carlos" value={filtros.comuna} onChange={handleChange('comuna')} />
            </div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Nombre, apellido, rut o ubicación</label>
              <input type="text" placeholder="Ej: González Pérez" value={filtros.q} onChange={handleChange('q')} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.2rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Foja</label>
              <input type="number" value={filtros.foja} onChange={handleChange('foja')} />
            </div>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Número</label>
              <input type="number" value={filtros.numero} onChange={handleChange('numero')} />
            </div>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.8rem' }}>Año</label>
              <input type="number" value={filtros.anio} onChange={handleChange('anio')} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" className="btn btn-primary" disabled={loadingBusqueda} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Search size={16} /> {loadingBusqueda ? 'Buscando...' : 'Buscar'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={limpiar} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <X size={16} /> Limpiar
            </button>
          </div>
        </form>
      </section>

      {/* Resultados */}
      {resultados && (
        <section className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0 }}>
              Resultados ({resultados.total})
            </h2>
            {resultados.last_page > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem' }}>
                <button className="btn btn-sm btn-secondary" disabled={pagina <= 1} onClick={() => buscar(pagina - 1)}>
                  <ChevronLeft size={14} />
                </button>
                Página {resultados.current_page} de {resultados.last_page}
                <button className="btn btn-sm btn-secondary" disabled={pagina >= resultados.last_page} onClick={() => buscar(pagina + 1)}>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>

          {resultados.data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Sin resultados para los criterios ingresados.
            </div>
          ) : (
            <div className="table-wrapper">
              <table style={{ width: '100%', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th>Folio Real</th>
                    <th>Folio Primitivo</th>
                    <th>Foja/Número/Año</th>
                    <th>Ubicación</th>
                    <th>Comuna</th>
                    <th>Dueño</th>
                    <th>Vendedor</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.data.map((r) => (
                    <tr key={r.id_folio_real}>
                      <td style={{ fontWeight: 700 }}>{r.id_folio_real}</td>
                      <td>{r.folio_primitivo_texto || '—'}</td>
                      <td>{r.foja ? `${r.foja}/${r.numero}/${r.anio}` : '—'}</td>
                      <td>{r.ubicacion || '—'}</td>
                      <td>{r.comuna || '—'}</td>
                      <td>{r.dueno_actual || '—'}</td>
                      <td>{r.vendedor_anterior || '—'}</td>
                      <td><EstadoFolioBadge estado={r.estado_folio} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-sm" onClick={() => verDetalle(r.id_folio_real)}>Ver detalle</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Detalle del folio */}
      {loadingDetalle && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <RefreshCw size={20} className="animate-spin" style={{ marginRight: '8px' }} /> Cargando folio...
        </div>
      )}

      {detalle && !loadingDetalle && (
        <section className="glass-card" style={{ padding: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--primary-dark)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Building2 size={20} /> Folio Real {detalle.inmueble.id_folio_real}
                <EstadoFolioBadge estado={detalle.inmueble.estado_folio} />
              </h2>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: '6px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MapPin size={14} /> {detalle.inmueble.ubicacion || 'Sin ubicación registrada'} — {detalle.inmueble.comuna || 'Sin comuna'}
              </p>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={() => setSearchParams({})}>Cerrar</button>
          </div>

          {/* Datos del inmueble */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem', fontSize: '0.82rem' }}>
            <div>
              <strong style={{ color: 'var(--text-muted)' }}>Tipo de propiedad</strong>
              <div>
                {detalle.inmueble.propiedad_plena === null ? '—'
                  : detalle.inmueble.propiedad_plena ? 'Propiedad Plena' : 'Nuda Propiedad'}
              </div>
            </div>
            <div>
              <strong style={{ color: 'var(--text-muted)' }}>Roles de avalúo</strong>
              <div>{[detalle.inmueble.rol_avaluo, detalle.inmueble.rol_avaluo_2, detalle.inmueble.rol_avaluo_3, detalle.inmueble.rol_avaluo_4].filter(Boolean).join(', ') || '—'}</div>
            </div>
            <div>
              <strong style={{ color: 'var(--text-muted)' }}>Superficie</strong>
              <div>{detalle.inmueble.superficie_hectareas ? `${detalle.inmueble.superficie_hectareas} ha` : '—'}</div>
            </div>
            <div>
              <strong style={{ color: 'var(--text-muted)' }}>Folio Primitivo</strong>
              <div>
                {detalle.folio_primitivo ? (
                  <button
                    className="btn btn-sm"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px' }}
                    onClick={() => verDetalle(detalle.folio_primitivo.id_folio_real)}
                  >
                    <Link2 size={12} /> Folio {detalle.folio_primitivo.id_folio_real}
                  </button>
                ) : detalle.inmueble.folio_primitivo_texto ? (
                  <span title="El folio primitivo no se pudo resolver a un folio real existente">
                    {detalle.inmueble.folio_primitivo_texto} <span className="badge badge-warning" style={{ fontSize: '0.65rem' }}>no resuelto</span>
                  </span>
                ) : '—'}
              </div>
            </div>
            {detalle.lotes_derivados?.length > 0 && (
              <div>
                <strong style={{ color: 'var(--text-muted)' }}>Lotes derivados de este folio</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                  {detalle.lotes_derivados.map((l) => (
                    <button key={l.id_folio_real} className="btn btn-sm btn-secondary" style={{ padding: '2px 8px' }} onClick={() => verDetalle(l.id_folio_real)}>
                      {l.id_folio_real}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Historial de dominio */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.8rem' }}>
              <Users size={16} /> Historial de Dominio ({detalle.inscripciones.length})
            </h3>
            {detalle.inscripciones.length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Sin inscripciones registradas.</p>
            ) : (
              <div className="table-wrapper">
                <table style={{ width: '100%', fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Tipo</th>
                      <th>Foja/Número/Año</th>
                      <th>Titular</th>
                      <th>Situación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.inscripciones.map((i) => (
                      <tr key={i.id_inscripcion}>
                        <td>{i.fecha_inscripcion}</td>
                        <td>{i.tipo_inscripcion?.descripcion || '—'}</td>
                        <td>{i.foja}/{i.numero}/{i.anio}</td>
                        <td>{nombrePropietario(i)}</td>
                        <td><VigenteBadge vigente={i.vigente} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Gravámenes propios */}
          <div style={{ marginBottom: detalle.gravamenes_heredados?.length > 0 ? '1.5rem' : 0 }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--primary-dark)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.8rem' }}>
              <Landmark size={16} /> Gravámenes y Prohibiciones ({detalle.gravamenes.length})
            </h3>
            {detalle.gravamenes.length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Sin gravámenes registrados.</p>
            ) : (
              <div className="table-wrapper">
                <table style={{ width: '100%', fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th>Naturaleza</th>
                      <th>Foja/Número/Año</th>
                      <th>Acreedor</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.gravamenes.map((g) => (
                      <tr key={g.id_gravamen}>
                        <td style={{ textTransform: 'capitalize' }}>{g.tipo_gravamen}</td>
                        <td>{g.tipo_inscripcion?.descripcion || '—'}</td>
                        <td>{g.foja}/{g.numero}/{g.anio}</td>
                        <td>{g.acreedor?.nombre_institucion || '—'}</td>
                        <td>{g.fecha_inscripcion || '—'}</td>
                        <td><AlzadoBadge alzado={g.estado_alzamiento} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Gravámenes heredados del folio primitivo (arrastre automático) */}
          {detalle.gravamenes_heredados?.length > 0 && (
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--copper)', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.4rem' }}>
                <Link2 size={16} /> Gravámenes heredados del folio primitivo ({detalle.gravamenes_heredados.length})
              </h3>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.8rem' }}>
                Vigentes en el predio madre, arrastrados automáticamente a esta subdivisión.
              </p>
              <div className="table-wrapper">
                <table style={{ width: '100%', fontSize: '0.8rem' }}>
                  <thead>
                    <tr>
                      <th>Heredado de</th>
                      <th>Tipo</th>
                      <th>Naturaleza</th>
                      <th>Foja/Número/Año</th>
                      <th>Acreedor</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalle.gravamenes_heredados.map((g) => (
                      <tr key={`heredado-${g.id_gravamen}`}>
                        <td>
                          <button className="btn btn-sm btn-secondary" style={{ padding: '2px 8px' }} onClick={() => verDetalle(g.heredado_de_folio_real)}>
                            Folio {g.heredado_de_folio_real}
                          </button>
                        </td>
                        <td style={{ textTransform: 'capitalize' }}>{g.tipo_gravamen}</td>
                        <td>{g.tipo_inscripcion?.descripcion || '—'}</td>
                        <td>{g.foja}/{g.numero}/{g.anio}</td>
                        <td>{g.acreedor?.nombre_institucion || '—'}</td>
                        <td><AlzadoBadge alzado={g.estado_alzamiento} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}